import { useState } from 'react';
import { FiMenu, FiMoon, FiSun, FiX } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import useThemeSwitcher from '../../hooks/useThemeSwitcher';
import { useI18n } from '../../context/I18nContext';
import HireMeModal from '../HireMeModal';
import { motion } from 'framer-motion';
import Button from '../reusable/Button';

const AppHeader = () => {
	const [showMenu, setShowMenu] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [activeTheme, setTheme] = useThemeSwitcher();
	const { t, toggleLang, lang } = useI18n();

	function toggleMenu() {
		if (!showMenu) {
			setShowMenu(true);
		} else {
			setShowMenu(false);
		}
	}

	function showHireMeModal() {
		if (!showModal) {
			document
				.getElementsByTagName('html')[0]
				.classList.add('overflow-y-hidden');
			setShowModal(true);
		} else {
			document
				.getElementsByTagName('html')[0]
				.classList.remove('overflow-y-hidden');
			setShowModal(false);
		}
	}

	return (
		<motion.nav
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			id="nav"
			className="sm:container sm:mx-auto"
		>
			<div className="z-10 max-w-screen-lg xl:max-w-screen-xl block sm:flex sm:justify-between sm:items-center py-6">
				{/* Header menu links and small screen hamburger menu */}
				<div className="flex justify-between items-center px-4 sm:px-0">
					<div>
						<Link to="/" className="flex items-center gap-2.5 shrink-0">
							<img src="/favicon.png" alt="Akrealities" className="w-9 h-9 object-contain shrink-0 rounded-full" />
							<p className="font-general-semibold text-lg whitespace-nowrap text-primary-dark dark:text-primary-light">
								Ibrahim <span className="text-brand-500">Akgül</span>
							</p>
						</Link>
					</div>

					{/* Theme + Language switchers small screen */}
					<div
						onClick={() => setTheme(activeTheme)}
						aria-label="Theme Switcher"
						className="block sm:hidden ml-0 bg-primary-light dark:bg-ternary-dark p-3 shadow-sm rounded-xl cursor-pointer"
					>
						{activeTheme === 'dark' ? (
							<FiMoon className="text-ternary-dark hover:text-gray-400 dark:text-ternary-light dark:hover:text-primary-light text-xl" />
						) : (
							<FiSun className="text-gray-200 hover:text-gray-50 text-xl" />
						)}
					</div>

					<button
						onClick={toggleLang}
						aria-label="Language Switcher"
						className="block sm:hidden ml-6 bg-primary-light dark:bg-ternary-dark p-2.5 shadow-sm rounded-xl cursor-pointer flex items-center"
						title={lang === 'en' ? 'Switch to French' : 'Passer en anglais'}
					>
						<span className="inline-block w-3 h-2 overflow-hidden rounded-sm align-middle">
							{lang === 'en' ? (
								<img src="https://flagcdn.com/w40/gb.png" alt="EN" className="w-full h-full object-contain" />
							) : (
								<img src="https://flagcdn.com/w40/fr.png" alt="FR" className="w-full h-full object-contain" />
							)}
						</span>
						<span className="ml-1 text-xs font-medium text-ternary-dark dark:text-ternary-light align-middle">
							{lang === 'en' ? 'FR' : 'EN'}
						</span>
					</button>

					{/* Small screen hamburger menu */}
					<div className="sm:hidden">
						<button
							onClick={toggleMenu}
							type="button"
							className="focus:outline-none"
							aria-label="Hamburger Menu"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 24 24"
								className="h-7 w-7 fill-current text-secondary-dark dark:text-ternary-light"
							>
								{showMenu ? (
									<FiX className="text-3xl" />
								) : (
									<FiMenu className="text-3xl" />
								)}
							</svg>
						</button>
					</div>
				</div>

				{/* Header links small screen */}
				<div
					className={
						showMenu
							? 'block m-0 sm:ml-4 mt-5 sm:mt-3 sm:flex p-5 sm:p-0 justify-center items-center shadow-lg sm:shadow-none'
							: 'hidden'
					}
				>
					<Link
						to="/projects"
						className="block text-left text-lg text-primary-dark dark:text-ternary-light hover:text-secondary-dark dark:hover:text-secondary-light  sm:mx-4 mb-2 sm:py-2"
						aria-label="Projects"
					>
						{t('projects')}
					</Link>
					<Link
						to="/about"
						className="block text-left text-lg text-primary-dark dark:text-ternary-light hover:text-secondary-dark dark:hover:text-secondary-light  sm:mx-4 mb-2 sm:py-2 border-t-2 pt-3 sm:pt-2 sm:border-t-0 border-primary-light dark:border-secondary-dark"
						aria-label="About Me"
					>
						{t('about')}
					</Link>
					<Link
						to="/contact"
						className="block text-left text-lg text-primary-dark dark:text-ternary-light hover:text-secondary-dark dark:hover:text-secondary-light  sm:mx-4 mb-2 sm:py-2 border-t-2 pt-3 sm:pt-2 sm:border-t-0 border-primary-light dark:border-secondary-dark"
						aria-label="Contact"
					>
						{t('contact')}
					</Link>
					<div className="border-t-2 pt-3 sm:pt-0 sm:border-t-0 border-primary-light dark:border-secondary-dark">
						<Link
							to="/contact"
							className="font-general-medium sm:hidden block text-left text-md clay-btn-primary px-4 py-2 mt-2 w-28 text-center"
							aria-label="Hire Me Button"
						>
							{t('hireMe')}
						</Link>
					</div>
				</div>

				{/* Header links large screen */}
				<div className="font-general-medium hidden m-0 sm:ml-4 mt-5 sm:mt-3 sm:flex p-5 sm:p-0 justify-center items-center shadow-lg sm:shadow-none">
					<Link
						to="/projects"
						className="block text-left text-lg text-primary-dark dark:text-ternary-light hover:text-secondary-dark dark:hover:text-secondary-light  sm:mx-4 mb-2 sm:py-2"
						aria-label="Projects"
					>
						{t('projects')}
					</Link>
					<Link
						to="/about"
						className="block text-left text-lg text-primary-dark dark:text-ternary-light hover:text-secondary-dark dark:hover:text-secondary-light  sm:mx-4 mb-2 sm:py-2"
						aria-label="About Me"
					>
						{t('about')}
					</Link>
					<Link
						to="/contact"
						className="block text-left text-lg text-primary-dark dark:text-ternary-light hover:text-secondary-dark dark:hover:text-secondary-light  sm:mx-4 mb-2 sm:py-2"
						aria-label="Contact"
					>
						{t('contact')}
					</Link>
				</div>

				{/* Header right section buttons */}
				<div className="hidden sm:flex justify-between items-center flex-col md:flex-row">
					<div className="hidden md:flex">
						<Link
							to="/contact"
							className="text-md font-general-medium clay-btn-primary px-5 py-2.5"
							aria-label="Hire Me Button"
						>
							{t('hireMe')}
						</Link>
					</div>

					{/* Theme switcher large screen */}
					<div className="flex items-center">
						<div
							onClick={() => setTheme(activeTheme)}
							aria-label="Theme Switcher"
							className="ml-8 bg-primary-light dark:bg-ternary-dark p-3 shadow-sm rounded-xl cursor-pointer"
						>
							{activeTheme === 'dark' ? (
								<FiMoon className="text-ternary-dark hover:text-gray-400 dark:text-ternary-light dark:hover:text-primary-light text-xl" />
							) : (
								<FiSun className="text-gray-200 hover:text-gray-50 text-xl" />
							)}
						</div>
						<button
							onClick={toggleLang}
							aria-label="Language Switcher"
							className="ml-6 bg-primary-light dark:bg-ternary-dark p-2.5 shadow-sm rounded-xl cursor-pointer flex items-center"
							title={lang === 'en' ? 'Switch to French' : 'Passer en anglais'}
						>
							<span className="inline-block w-3 h-2 overflow-hidden rounded-sm align-middle">
								{lang === 'en' ? (
									<img src="https://flagcdn.com/w40/fr.png" alt="FR" className="w-full h-full object-contain" />
								) : (
									<img src="https://flagcdn.com/w40/gb.png" alt="EN" className="w-full h-full object-contain" />
								)}
							</span>
							<span className="ml-1 text-xs font-medium text-ternary-dark dark:text-ternary-light align-middle">
								{lang === 'en' ? 'FR' : 'EN'}
							</span>
						</button>
					</div>
				</div>
			</div>
			{/* Hire me modal removed: navigating to Contact page instead */}
		</motion.nav>
	);
};

export default AppHeader;
